
import React from "react";
import { FunctionField } from "react-admin";
import { Box, Button as MuiButton, SvgIcon } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { ReactComponent as SsnIcon } from "../../../assets/images/ssn.svg";
import { typesofUsers } from "../../../config";
import { shouldHideSystemAssessment, shouldShowReviewPending } from "../../../utils/screeningScore";

export const isAdminRole = (userrole?: string) =>
  userrole === "Docuverus Super Admin" || userrole === "Docuverus Admin";

export const canShowDocumentNewTag = (userrole?: string) =>
  userrole === typesofUsers.docuverusSuperAdmin;

export const resolveAppType = (
  appType: string | number | undefined,
  filterValues: any
) => {
  const t = (appType ?? filterValues?.app_invitation_type) as
    | string
    | number
    | undefined;
  return t != null ? String(t) : undefined;
};

export const canViewPIIForId = (
  resolvedType: string | undefined,
  permissions: any,
  userrole?: string,
  isSuperAdmin: (allowed: boolean, role?: string) => boolean = (x) => x,
  checkModuleAccess: (perms: any, menu: string, action: string) => boolean = () => false
) => {
  if (!resolvedType) return false;
  if (isAdminRole(userrole)) return true;

  if (resolvedType === "1") {
    return isSuperAdmin(checkModuleAccess(permissions, "Inbox", "View PII"), userrole);
  }
  if (resolvedType === "2") {
    return isSuperAdmin(checkModuleAccess(permissions, "Doc Upload App", "View PII"), userrole);
  }
  if (resolvedType === "3") {
    return isSuperAdmin(checkModuleAccess(permissions, "Guest Card App", "View PII"), userrole);
  }
  return false;
};


export function BasicMenu({
  socrStatus,
  documentType,
}: {
  socrStatus?: string;
  documentType?: string;
}) {
  const hasStatus = socrStatus !== undefined && socrStatus !== null && String(socrStatus).trim() !== "";
  const status = hasStatus ? String(socrStatus) : documentType === "ID" ? "NA" : "Verification Required";
  const isValidLinked = status === "Valid - Linked";
  const isValidStatus = status === "Valid" || isValidLinked;
  const isSsnPresent = documentType === "SSN" && status === "Verification Required";
  const title = isSsnPresent
    ? "Present"
    : status === "Verification Required"
    ? "Verif req."
    : status;
  const cls = isSsnPresent
    ? "text-info-soft"
    : status === "Invalid"
    ? "text-danger"
    : isValidStatus
    ? "text-success"
    : "text-warning";

  return (
    <div
      title={title}
      className={`d-flex flex-column align-items-center ${cls}`}
      style={{ padding: "0 10px", marginLeft: "5px" }}
    >
      <p style={{ padding: "0 10px" }}>
        {isSsnPresent ? (
          <SvgIcon
            component={SsnIcon}
            inheritViewBox
            titleAccess="SSN status"
            aria-label="SSN status"
            style={{ width: "18px", height: "18px" }}
          />
        ) : status === "Invalid" ? (
          <ErrorIcon color="error" />
        ) : isValidStatus ? (
          <CheckIcon color="success" />
        ) : (
          <ReportProblemIcon color="warning" />
        )}
      </p>
      {isSsnPresent
        ? "Present"
        : status === "Verification Required"
        ? "Verif req."
        : isValidLinked
        ? "Valid - Linked"
        : `${status.substring(0, 10)}${status.length > 10 ? "..." : ""}`}
    </div>
  );
}


export function DocumentField({
  label,
  source,
  filterValues,
  presentKey,
  requiredKey,
  newTagKey,
  socrStatusKey,
  documentType,
  onClick,
  hideOnGuestCard = false,
  noLabel,
  afterStatusWhenInfoPresent,
  infoPresentOverride,
  socrStatusOverride,
  viewerRole,
  applicationStatus,
  reviewedKey,
}: {
  label: string;
  source: string;
  filterValues: any;
  presentKey: string;                // "IDS" | "SSN" | "Income" | "Banking" | "Misc"
  requiredKey?: string;              // "IsRequiredID" | "IsRequiredSSN" | ...
  newTagKey?: string;                // "NewTagID" | "NewTagSSN" | ...
  socrStatusKey?: string;            // "IDSOCRStatus" | "SSNSOCRStatus" | ...
  documentType: string;              // "ID" | "SSN" | "Paystub" | "Bank Statement" | "Other"
  onClick?: (e: any, record: any, documentType: string) => void;
  hideOnGuestCard?: boolean;         // hide when app type is "3"
  noLabel?: string;                  // default "Doc Req"
  afterStatusWhenInfoPresent?: (record: any) => React.ReactNode; // extra UI under the 'Status' state
  infoPresentOverride?: (record: any) => boolean | undefined;
  socrStatusOverride?: (record: any) => string | undefined;
  viewerRole?: string;
  applicationStatus?: string;        // authoritative parent application status (preferred over the row's)
  reviewedKey?: string;              // record field name for the SP-computed section reviewed flag (e.g. "IDReviewed")
}) {
  const resolvedType = resolveAppType(undefined, filterValues);
  if (hideOnGuestCard && resolvedType === "3") return null;

  return (
    <FunctionField
      source={source}
      label={label}
      style={{ minWidth: "100px" }}
      render={(record: any) => {
        const status = record?.ApplicantStatus;
        if (status === "Invited" || status === "Removed") return null;

        // Story 4622: hide system-determined assessments from subscribers until a
        // Docuverus Super Admin Reviewer has completed manual review. Prefer the
        // authoritative parent application status passed by the call-site; fall back
        // to the row's ApplicationStatus when the parent record context is absent
        // (e.g. standalone DocumentField renders). ApplicationStatus is an app-level
        // field denormalized onto every applicant row, so the fallback resolves to the
        // same status — not the per-applicant ApplicantStatus.
        if (
          shouldHideSystemAssessment(
            applicationStatus ?? record?.ApplicationStatus,
            viewerRole
          )
        )
          return null;

        const overriddenInfoPresent = infoPresentOverride?.(record);
        const infoPresent =
          typeof overriddenInfoPresent === "boolean"
            ? overriddenInfoPresent
            : record?.[presentKey] === "Yes";
        const isNew =
          canShowDocumentNewTag(viewerRole) && newTagKey ? !!record?.[newTagKey] : false;
        const isReviewed = reviewedKey ? (record?.[reviewedKey] as boolean | undefined) : true;
        const reviewPending = shouldShowReviewPending(isReviewed, viewerRole);
        const overriddenSocrStatus = socrStatusOverride?.(record);
        const socrStatus =
          overriddenSocrStatus ??
          (socrStatusKey ? record?.[socrStatusKey] : undefined);
        const isMissingBankingDocument = documentType === "Bank Statement" && !infoPresent;
        const isRequired =
          (requiredKey ? !!record?.[requiredKey] : false) ||
          (!infoPresent && socrStatus === "Verification Required") ||
          isMissingBankingDocument;

        return (
          <Box>
            {!infoPresent && (
              <div className="d-flex flex-column align-items-center">
                <p style={{ padding: "0 10px" }}>
                  <CloseIcon />
                </p>
                {isRequired && (
                  <span style={{ color: "orange", padding: "0 10px" }}>
                    {noLabel ?? "Doc Req"}
                  </span>
                )}
              </div>
            )}

            {infoPresent && (
              <div
                className="d-flex flex-column align-items-center"
                onClick={
                  socrStatus !== undefined && onClick && !reviewPending
                    ? (event) => onClick(event, record, documentType)
                    : undefined
                }
                style={
                  socrStatus !== undefined && onClick && !reviewPending
                    ? { cursor: "pointer" }
                    : undefined
                }
              >
                {reviewPending ? (
                  <span style={{ fontSize: 11, color: "#888", padding: "0 6px" }}>Review Pending</span>
                ) : socrStatus !== undefined ? (
                  <BasicMenu socrStatus={socrStatus} documentType={documentType} />
                ) : (
                  onClick && (
                    <MuiButton
                      sx={{
                        justifyContent: "center",
                        padding: "0 10px",
                        display: "flex",
                        flexDirection: "column",
                        "& .MuiButton-startIcon.MuiButton-iconSizeMedium": { margin: 0 },
                      }}
                      startIcon={<CheckIcon color="primary" />}
                      onClick={(event) => onClick(event, record, documentType)}
                    >
                      View
                    </MuiButton>
                  )
                )}
                {afterStatusWhenInfoPresent ? afterStatusWhenInfoPresent(record) : null}
              </div>
            )}

            <div className="badge-cell d-flex flex-column align-items-center">
              {isNew ? <em>New</em> : <></>}
            </div>
          </Box>
        );
      }}
    />
  );
}
